import { apiClient } from "./apiClient";
import type { ApiResponse, Client, Car, EffortLevel, ClientBirthdayRow } from "../types";

export interface ClientFormData {
  name: string;
  phoneNumber?: string;
  birthday?: string;
  address?: string;
  cpf?: string;
}
export interface CarFormData {
  clientId: number;
  model: string;
  plate: string;
  brand?: string | null;
  effort?: EffortLevel;
}

export const clientsApi = {
  /**
   * Get all clients for a company
   */
  /**
   * Clients with birthdays in a date range (inclusive start/end, YYYY-MM-DD).
   * GET /client/birthdays?start=&end=
   */
  getClientBirthdays: async (params: {
    start: string;
    end: string;
  }): Promise<ApiResponse<ClientBirthdayRow[]>> => {
    try {
      const q = new URLSearchParams({
        startDate: params.start,
        endDate: params.end,
      });
      const data = await apiClient.get<any>(`/clients/birthdays?${q.toString()}`);
      const list = Array.isArray(data) ? data : data?.data ?? [];
      return {
        success: true,
        data: list.clients,
      };
    } catch (error) {
      console.error("Error fetching client birthdays:", error);
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Failed to fetch birthdays",
      };
    }
  },

  getClientsByCompany: async (): Promise<ApiResponse<Client[]>> => {
    try {
      const data = await apiClient.get<any>(`/clients`);

      return {
        success: true,
        data: data.data,
      };
    } catch (error) {
      console.error("Error fetching clients:", error);
      return {
        success: false,
        error: "Failed to fetch clients",
      };
    }
  },

  /**
   * Get a specific client by ID
   */
  getClientById: async (clientId: number): Promise<ApiResponse<Client>> => {
    try {
      const data = await apiClient.get<any>(`/clients/${clientId}`);

      return {
        success: true,
        data: data.data,
      };
    } catch (error) {
      console.error("Error fetching client:", error);
      return {
        success: false,
        error: "Failed to fetch client",
      };
    }
  },

  /**
   * Create a new client
   */
  createClient: async (
    clientData: ClientFormData
  ): Promise<ApiResponse<Client>> => {
    try {
      const data = await apiClient.post<ApiResponse<Client>>("/clients", clientData);

      return {
        success: true,
        data: data.data,
      };
    } catch (error) {
      console.error("Error creating client:", error);
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : "Failed to create client",
      };
    }
  },

  /**
   * Update an existing client
   */
  updateClient: async (
    clientId: number,
    clientData: Partial<ClientFormData>
  ): Promise<ApiResponse<Client>> => {
    try {
      const data = await apiClient.put<Client>(
        `/clients/${clientId}`,
        clientData
      );

      return {
        success: true,
        data: data,
      };
    } catch (error) {
      console.error("Error updating client:", error);
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : "Failed to update client",
      };
    }
  },

  /**
   * Delete a client (soft delete - sets isActive to false)
   */
  deleteClient: async (
    clientId: number
  ): Promise<ApiResponse<{ message: string }>> => {
    try {
      const data = await apiClient.delete<{ message: string }>(
        `/clients/${clientId}`
      );

      return {
        success: true,
        data: data,
      };
    } catch (error) {
      console.error("Error deleting client:", error);
      return {
        success: false,
        error: "Failed to delete client",
      };
    }
  },

  /**
   * Search clients by name or phone
   */
  searchClients: async (
    companyId: number,
    query: string
  ): Promise<ApiResponse<Client[]>> => {
    try {
      const data = await apiClient.get<any>(
        `/companies/${companyId}/clients/search?q=${encodeURIComponent(query)}`
      );

      return {
        success: true,
        data: data,
      };
    } catch (error) {
      console.error("Error searching clients:", error);
      return {
        success: false,
        error: "Failed to search clients",
      };
    }
  },

  /**
   * Get all cars for a client
   */
  getCarsByClient: async (clientId: number): Promise<ApiResponse<Car[]>> => {
    try {
      const data = await apiClient.get<any>(`/cars/client/${clientId}`);

      return {
        success: true,
        data: data.data,
      };
    } catch (error) {
      console.error("Error fetching cars:", error);
      return {
        success: false,
        error: "Failed to fetch cars",
      };
    }
  },

  /**
   * Create a new car for a client
   */
  createCar: async (carData: CarFormData): Promise<ApiResponse<Car>> => {
    try {
      const data = await apiClient.post<Car>("/cars", carData);

      return {
        success: true,
        data: data,
      };
    } catch (error) {
      console.error("Error creating car:", error);
      return {
        success: false,
        error: "Este carro já está cadastrado para este cliente",
      };
    }
  },

  /**
   * Update an existing car
   */
  updateCar: async (
    carId: number,
    carData: Partial<CarFormData>
  ): Promise<ApiResponse<Car>> => {
    try {
      const data = await apiClient.put<Car>(`/cars/${carId}`, carData);

      return {
        success: true,
        data: data,
      };
    } catch (error) {
      console.error("Error updating car:", error);
      return {
        success: false,
        error: "Este carro já está cadastrado para este cliente",
      };
    }
  },

  /**
   * Delete a car
   */
  deleteCar: async (
    carId: number
  ): Promise<ApiResponse<{ message: string }>> => {
    try {
      const data = await apiClient.delete<{ message: string }>(
        `/cars/${carId}`
      );

      return {
        success: true,
        data: data,
      };
    } catch (error) {
      console.error("Error deleting car:", error);
      return {
        success: false,
        error: "Failed to delete car",
      };
    }
  },
};
